import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import asdModel from './assets/asd-figure.glb?url';
import { MenuOptionsTitleView, OptionTitleData } from './menu-options-title-view';
import gsap from 'gsap';
import { MenuOptionProps, OptionTitle } from './menu-datas';
import { Disposable } from '@tools/interfaces/disposable';
import AbortPlugin from '@tools/async/gsap-abort-plugin';
import { wait, waitForSeconds, waitUntil } from '@tools/async/awaitable';
import { isSafariWebView, isIpad } from '@tools/browsers';
import { MenuTitleText } from './menu-title-text';
import { Material } from 'three';
import { MenuCombineButton } from './menu-combine-button';

gsap.registerPlugin(AbortPlugin);
export class MenuThreeView implements Disposable {
  private abort = new AbortController();
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private optionsTitleView?: MenuOptionsTitleView;
  private props: MenuOptionProps[] = [];
  private figureMaterial?: Material;
  private combineButton?: MenuCombineButton;

  private animationMixer?: THREE.AnimationMixer;
  private modelAnimation?: THREE.AnimationAction;
  private clock = new THREE.Clock();

  private modelContainer = new THREE.Group();
  private currentModel?: THREE.Group;
  private pointer = new THREE.Vector2();

  private raycaster = new THREE.Raycaster();
  private touchZones: THREE.Object3D[] = [];

  private raycastTarget?: THREE.Object3D;
  public onClickOption: (linkPart: string, blank: boolean) => void = () => {};
  public onClickClose: () => void = () => {};
  private isShow = false;

  constructor(container: HTMLElement, optionsProps: MenuOptionProps[], defaultOptionTitle: OptionTitle) {
    this.props = optionsProps;
    this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.scene.add(ambientLight);

    const light = new THREE.DirectionalLight(0xccffff, 3);
    light.rotateY(-Math.PI / 3);
    light.position.set(0, 6, 10);
    this.scene.add(light);

    this.scene.add(this.modelContainer);

    const loader = new GLTFLoader();
    const setModel = this.setModel.bind(this);
    loader.load(
      asdModel,
      function (gltf: GLTF) {
        setModel(gltf.scene, gltf.animations[0], defaultOptionTitle);
      }.bind(this),
      undefined,
      function (error) {
        console.error(error);
      }
    );

    this.camera.position.set(0, -0.1, 5);
    window.addEventListener('resize', this.onResize);
  }

  dispose(): void {
    this.scene.clear();
    this.renderer.dispose();
    this.abort.abort();
  }

  private onClick = () => {
    if (!this.raycastTarget) {
      this.onClickClose();
      return;
    }

    if (this.raycastTarget === this.combineButton?.model) {
      void this.combineAnimation();
    } else {
      const option = this.props.find((o) => o.linkPart === this.raycastTarget!.name);
      if (option) this.onClickOption(option.linkPart, option.blank);

      //auto refresh material when stay on the same page
      if (option?.blank) {
        (this.raycastTarget! as THREE.Mesh).material = this.figureMaterial!;
        this.props[this.props.indexOf(option)].actived = true;
        this.combineButton?.setVisible(this.props.every((o) => o.actived));
      }
    }
  };

  private onMouseMove = (event: MouseEvent) => {
    this.pointer.x = event.clientX - window.innerWidth / 2;
    this.pointer.y = event.clientY - window.innerHeight / 2;
    this.raycast(
      new THREE.Vector2((event.clientX / window.innerWidth) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1)
    );
  };

  private async setModel(model: THREE.Group, anim: THREE.AnimationClip, defaultOptionTitle: OptionTitle) {
    model.scale.set(0.47, 0.47, 0.47);
    this.modelContainer.add(model);
    this.animationMixer = new THREE.AnimationMixer(model);

    this.modelAnimation = this.animationMixer.clipAction(anim);
    this.modelAnimation.timeScale = 1.8;
    this.modelAnimation.loop = THREE.LoopOnce;
    this.modelAnimation.clampWhenFinished = true;

    this.currentModel = model;
    this.touchZones = model.children.filter((o) => o.name.includes('-zone'));

    this.figureMaterial = (model.getObjectByName('head')! as THREE.Mesh).material as Material;
    const optionTitleDatas = this.props.map((option) => {
      const zone = this.touchZones.find((z) => z.name.includes(option.linkPart));
      if (!zone) {
        console.warn(`Touch zone for ${option.title} not found`);
        return null;
      }
      if (option.actived) (this.getTargetByTouchZone(zone) as THREE.Mesh).material = this.figureMaterial!;

      return {
        title: option.title,
        position: new THREE.Vector2(zone.position.x, zone.position.y),
      };
    }) as OptionTitleData[];

    new MenuTitleText(model);
    this.optionsTitleView = new MenuOptionsTitleView(model, optionTitleDatas, defaultOptionTitle);

    this.combineButton = new MenuCombineButton(this.props.every((o) => o.actived));

    await waitUntil(() => this.combineButton?.model !== undefined, this.abort.signal);
    const btn = this.combineButton!.model!;
    model.add(btn);
    this.touchZones.push(btn);
    btn.position.set(0, -6.3, 0.5);
  }

  private raycast(coordinate: THREE.Vector2) {
    if (!this.currentModel) return;

    this.raycaster.setFromCamera(coordinate, this.camera);
    const intersects = this.raycaster.intersectObjects(this.touchZones);

    if (intersects.length <= 0) {
      if (this.raycastTarget) this.animateScaleObject(this.raycastTarget, 1, 0.1);
      this.raycastTarget = undefined;
      document.body.style.cursor = 'default';
      return;
    }

    const intersectedObject = intersects[0].object;
    const target =
      intersectedObject === this.combineButton?.model
        ? intersectedObject
        : this.getTargetByTouchZone(intersectedObject);
    if (this.raycastTarget === target) return;

    document.body.style.cursor = 'pointer';
    if (this.raycastTarget) this.animateScaleObject(this.raycastTarget, 1, 0.1);
    this.raycastTarget = target;
    this.animateScaleObject(target, 1.03, 0.2, 'back.out(8)');
    if (target !== this.combineButton?.model)
      this.optionsTitleView?.selectOption(this.props.find((o) => o.linkPart === target.name)!.title);
  }

  private getTargetByTouchZone(touchZone: THREE.Object3D): THREE.Object3D {
    const target = this.currentModel!.getObjectByName(touchZone.name.replace('-zone', ''));
    return target!;
  }

  private animateScaleObject(obj: THREE.Object3D, scale: number, duration: number, ease?: string) {
    if (!obj) return;

    gsap.killTweensOf(obj.scale);
    gsap.to(obj.scale, { x: scale, y: scale, z: scale, duration, ease, abort: { signal: this.abort.signal } });
  }

  private modelLookAtMouse() {
    if (!this.currentModel) return;

    const target = { x: this.pointer.x * 0.001, y: this.pointer.y * 0.001 };

    this.currentModel.rotation.set(0, 0, 0);
    this.currentModel.rotation.x += 0.2 * (target.y - this.currentModel.rotation.x);
    this.currentModel.rotation.y += 0.2 * (target.x - this.currentModel.rotation.y);
  }

  public async show(): Promise<boolean> {
    const signal = this.abort.signal;

    if (!this.isShow) {
      this.isShow = true;

      await waitUntil(() => this.currentModel !== undefined, signal);
      await wait(100, signal);

      const render = () => {
        this.modelLookAtMouse();
        this.renderer.render(this.scene, this.camera);
        const delta = this.clock.getDelta();
        this.animationMixer?.update(delta);
      };
      this.renderer.setAnimationLoop(render);

      window.addEventListener('mousemove', this.onMouseMove);

      this.optionsTitleView!.resetDefaultOption();
      this.modelContainer.visible = true;
      this.modelContainer.position.set(0, 0, 6);
      this.modelContainer.rotation.set(0, -Math.PI / 4, 0);
      gsap.to(this.modelContainer.position, { z: 0, duration: 0.4, abort: { signal } });
      gsap.to(this.modelContainer.rotation, {
        y: 0,
        duration: 0.5,
        ease: 'power1.inOut',
        abort: { signal },
        onComplete: () => {
          window.addEventListener('mouseup', this.onClick);
        },
      });
    } else {
      this.isShow = false;
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onClick);

      gsap.to(this.modelContainer.position, { z: 6, duration: 0.3, abort: { signal } });
      await gsap.to(this.modelContainer.rotation, {
        y: -Math.PI / 4,
        duration: 0.3,
        abort: { signal },
      });

      this.modelContainer.visible = false;
      this.raycastTarget = undefined;
      this.renderer.setAnimationLoop(null);
    }

    return this.isShow;
  }

  private async combineAnimation() {
    if (!this.currentModel || !this.modelAnimation) return;
    const signal = this.abort.signal;

    window.removeEventListener('mouseup', this.onClick);
    gsap.to(this.camera.position, {
      y: 0.3,
      z: 4.5,
      duration: 0.3,
      ease: 'back.out',
      abort: { signal },
    });

    this.combineButton?.outAnimation();
    this.modelAnimation.reset();
    this.modelAnimation.play();

    await waitForSeconds(0.6, signal);

    for (const zone of this.touchZones) {
      if (zone === this.combineButton?.model) continue;
      this.optionsTitleView?.moveText(
        this.props.find((o) => o.linkPart === zone.name.replace('-zone', ''))!.title,
        new THREE.Vector2(zone.position.x, zone.position.y)
      );
    }

    await waitForSeconds(0.2, signal);
    window.addEventListener('mouseup', this.onClick);
  }

  onResize = async () => {
    if (this.abort.signal.aborted) return;
    if (isSafariWebView() || isIpad()) await waitForSeconds(0.8, this.abort.signal); // Hack for webview. Safari webview sends inconsistent dimensions on resize on slower devices.

    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  };
}
